import { useCallback, useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import {
  Autocomplete,
  Modal,
  ModalBody,
  ModalFooterDefault,
  ModalHeader,
  Select,
  SelectOption,
} from 'ui-components';
import { Creator } from '~/clientApi/creator.model';
import { useSearchCreator } from '~/hooks/api/useSearchCreator';

import { TalentProfileProperties } from '../blocks/TalentProfile';
import { usePageBuilderStore } from '../hooks/usePageBuilderStore';
import { useSelectCreatorStore } from './useSelectCreatorStore';

export const ModalSelectCreator = () => {
  const { isModalOpen, setIsModalOpen, callback, mode } =
    useSelectCreatorStore();
  const { blocks } = usePageBuilderStore();

  const [input, setInput] = useState('');
  const [selectedCreator, setSelectedCreator] = useState<Creator>();

  const { data: creators } = useSearchCreator(input);
  const options = useMemo(() => {
    if (!creators) return [];

    return creators.map((c: Creator) => ({
      label: c.fullName,
      avatar: c.avatar,
      id: c, // TODO: Use slug ¯\_(ツ)_/¯
    }));
  }, [creators]);

  const creatorsFromBlocks = useMemo(() => {
    if (!blocks.length) return [];

    return blocks
      .filter((b) => b.component === 'TalentProfile')
      .map((b) => {
        const properties = b.properties as TalentProfileProperties;

        // The API flattens the snapshot onto `properties` — rebuild it from there.
        return (
          properties.creator ??
          ({
            id: properties.creatorId,
            fullName: properties.fullName,
            avatar: properties.avatar,
          } as Creator)
        );
      })
      .filter((c) => Boolean(c?.id));
  }, [blocks]);

  const handleSubmit = useCallback(
    (creator?: Creator) => {
      if (typeof callback === 'function') {
        callback(creator || (selectedCreator as Creator));
      }
    },
    [selectedCreator, callback],
  );

  useEffect(() => {
    if (mode === 'from-blocks' && creatorsFromBlocks.length === 1) {
      // Close the modal and auto select the creator
      handleSubmit(creatorsFromBlocks.at(0));
    }
  }, [mode, creatorsFromBlocks, handleSubmit]);

  return (
    <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
      <ModalHeader>Select Creator</ModalHeader>
      <ModalBody>
        {match(mode)
          .with('all', () => (
            <Autocomplete
              ariaLabel="Search creator"
              onSelectOption={(opt) => {
                setSelectedCreator(opt.id as unknown as Creator);
              }}
              onChange={setInput}
              options={options}
            />
          ))
          .with('from-blocks', () => (
            <Select
              onChange={(v) => {
                // @ts-ignore - bs error
                setSelectedCreator(v.value as Creator);
              }}
              placeholder="Select creator"
            >
              {creatorsFromBlocks.map((opt) => (
                // @ts-ignore - this is a bs error
                <SelectOption key={opt.id} value={opt}>
                  {opt.fullName}
                </SelectOption>
              ))}
            </Select>
          ))
          .exhaustive()}
      </ModalBody>
      <ModalFooterDefault
        primary={{
          title: 'Select',
          onClick: () => handleSubmit(),
        }}
      />
    </Modal>
  );
};
